import { Dispatch, SetStateAction, useMemo } from 'react';
import { ColumnDef, SortingState, OnChangeFn } from '@tanstack/react-table';
import {
  DataTable,
  TableSkeleton,
  Pagination,
  PaginationState,
} from '@/components';
import { EmptyState } from './components';
import { AnyMedicalRecord, MedicalRecordType } from './types';
import {
  createProviderNotesColumns,
  createImagingColumns,
  createProceduresColumns,
  createLabResultsColumns,
  createPathologyResultsColumns,
  createMedicationsColumns,
  createVitalsColumns,
  createProblemsColumns,
  createEncountersColumns,
  createAdditionalRecordsColumns,
  createOriginalRecordsColumns,
} from './columns';

interface MedicalRecordsTableProps {
  recordType: MedicalRecordType;
  records: AnyMedicalRecord[];
  totalCount: number;
  isLoading: boolean;
  pagination: PaginationState;
  onPaginationChange: Dispatch<SetStateAction<PaginationState>>;
  sorting: SortingState;
  onSortingChange: OnChangeFn<SortingState>;
}

export const MedicalRecordsTable = ({
  recordType,
  records,
  totalCount,
  isLoading,
  pagination,
  onPaginationChange,
  sorting,
  onSortingChange,
}: MedicalRecordsTableProps) => {
  const columns = useMemo(() => {
    switch (recordType) {
      case 'providerNotes':
        return createProviderNotesColumns();
      case 'imaging':
        return createImagingColumns();
      case 'procedures':
        return createProceduresColumns();
      case 'labResults':
        return createLabResultsColumns();
      case 'pathologyResults':
        return createPathologyResultsColumns();
      case 'medications':
        return createMedicationsColumns();
      case 'vitals':
        return createVitalsColumns();
      case 'problems':
        return createProblemsColumns();
      case 'encounters':
        return createEncountersColumns();
      case 'additionalRecords':
        return createAdditionalRecordsColumns();
      case 'originalRecords':
        return createOriginalRecordsColumns();
      default:
        return [] as ColumnDef<AnyMedicalRecord>[];
    }
  }, [recordType]);

  if (isLoading) {
    return (
      <div className="min-w-0 flex-1">
        <TableSkeleton rows={5} columns={columns.length || 5} />
      </div>
    );
  }

  const hasRecords = records.length > 0;

  // Show only empty state when no records - no table
  if (!hasRecords) {
    return (
      <div className="min-w-0 flex-1">
        <EmptyState />
      </div>
    );
  }

  return (
    <div className="min-w-0 flex-1">
      <DataTable
        columns={columns as ColumnDef<AnyMedicalRecord>[]}
        data={records}
        sorting={sorting}
        onSortingChange={onSortingChange}
      />
      <Pagination
        pagination={pagination}
        setPagination={onPaginationChange}
        totalCount={totalCount}
        pageSizeOptions={[10, 20, 50]}
      />
    </div>
  );
};
